import React, { useMemo, useState } from 'react';
import Link from './Link';
import ReturnAnalysis from './ReturnAnalysis';
import { fmt, fmtPercentPrecise } from './format';
import { aggregateArticles } from './salesCalculations';
import { cardStyle, cellStyle, headRowStyle, headStyle, headTextStyle, linkStyle, stripeStyle, tableStyle, textCellStyle } from './styles';

const compareText = (a, b) => (a || '').localeCompare(b || '', 'sv');

// Columns where text sorts ascending first and numbers descending first. Articles without a return rate (nothing sold) always end up last.
const COLUMNS = [
    { key: 'name', label: 'Artikel', text: true, compare: (a, b) => compareText(a.description || a.articleNumber, b.description || b.articleNumber) },
    { key: 'articleNumber', label: 'Artikelnr', text: true, compare: (a, b) => compareText(a.articleNumber, b.articleNumber) },
    { key: 'sold', label: 'Sålda', compare: (a, b) => a.sold - b.sold },
    { key: 'returned', label: 'Returnerade', compare: (a, b) => a.returned - b.returned },
    { key: 'rate', label: 'Returprocent', compare: (a, b) => a.rate - b.rate },
];

const sortableStyle = { cursor: 'pointer', userSelect: 'none' };

const DEFAULT_MIN_SOLD = 10;
// Return rates up to GOOD_RATE are green and from BAD_RATE red, with the colours in between running from green over yellow and orange to red
const GOOD_RATE = 0.01;
const BAD_RATE = 0.3;

const getRateColors = (rate) => {
    const position = Math.min(Math.max((rate - GOOD_RATE) / (BAD_RATE - GOOD_RATE), 0), 1);
    const hue = Math.round(120 * (1 - position));
    return { backgroundColor: `hsl(${hue}, 70%, 88%)`, color: `hsl(${hue}, 65%, 25%)` };
};

const rateBadgeStyle = (rate) => ({ ...getRateColors(rate), display: 'inline-block', minWidth: '4rem', padding: '0.1rem 0.5rem', borderRadius: '0.75rem', fontWeight: 600, textAlign: 'center' });

const filterInputStyle = { width: '5rem', height: '2rem', margin: '0 0.5rem', padding: '0 0.5rem', fontSize: '0.9rem', border: '1px solid #cacaca', borderRadius: '0.25rem' };

const analyzeButtonStyle = { padding: '0.2rem 0.8rem', border: '1px solid #1779ba', borderRadius: '1rem', backgroundColor: '#fff', color: '#1779ba', fontSize: '0.8rem', whiteSpace: 'nowrap', cursor: 'pointer', margin: 0 };

// Returned articles with the quantity sold on the web invoices and the return rate. The quantities link to the invoices behind them and the name to the article page.
const ReturnedArticles = ({ soldInvoices, creditInvoices, articleUrls, invoicesByNumber, onShowInvoices }) => {
    const [sort, setSort] = useState({ key: 'returned', descending: true });
    // Kept as text so that the field can be emptied while typing
    const [minSold, setMinSold] = useState(String(DEFAULT_MIN_SOLD));

    const articles = useMemo(() => {
        const sold = new Map(aggregateArticles(soldInvoices).map((article) => [article.key, article]));

        return aggregateArticles(creditInvoices, { absolute: true }).map((article) => {
            const soldArticle = sold.get(article.key);
            const soldQuantity = soldArticle?.quantity || 0;
            return {
                ...article,
                sold: soldQuantity,
                soldInvoices: soldArticle?.invoices || [],
                returned: article.quantity,
                returnedInvoices: article.invoices,
                rate: soldQuantity > 0 ? article.quantity / soldQuantity : null,
            };
        });
    }, [soldInvoices, creditInvoices]);

    const rows = useMemo(() => {
        const column = COLUMNS.find((c) => c.key === sort.key);
        const direction = sort.descending ? -1 : 1;
        const minimum = Number(minSold) || 0;
        return articles
            .filter((article) => article.sold >= minimum)
            .sort((a, b) => {
                if (column.key === 'rate' && (a.rate === null || b.rate === null)) return (a.rate === null) - (b.rate === null);
                return direction * column.compare(a, b) || b.returned - a.returned;
            });
    }, [articles, sort, minSold]);

    const toggleSort = (column) => setSort((prev) => (prev.key === column.key
        ? { key: column.key, descending: !prev.descending }
        : { key: column.key, descending: !column.text }));

    const showInvoices = (prefix, row, invoices) => onShowInvoices({ title: `${prefix}: ${row.description || row.articleNumber} (${row.articleNumber})`, invoices });

    const renderName = (row) => {
        const name = row.description || row.articleNumber;
        const url = articleUrls[row.articleNumber];
        return url ? <a href={url} target="_blank" rel="noopener noreferrer" style={linkStyle}>{name}</a> : name;
    };

    return (
        <>
        <div style={{ margin: '0 0 0.75rem', fontSize: '0.85rem' }}>
            <label>
                Visa artiklar med minst
                <input
                    type="number"
                    min="0"
                    value={minSold}
                    onChange={(e) => setMinSold(e.target.value)}
                    aria-label="Minsta antal sålda"
                    style={filterInputStyle}
                />
                sålda
            </label>
            <span style={{ marginLeft: '1rem', color: '#666' }}>
                {rows.length === articles.length ? `${articles.length} artiklar` : `${rows.length} av ${articles.length} artiklar`}
            </span>
        </div>
        <div style={cardStyle}>
            <table style={tableStyle}>
                <thead>
                    <tr style={headRowStyle}>
                        {COLUMNS.map((column) => {
                            const active = sort.key === column.key;
                            return (
                                <th
                                    key={column.key}
                                    style={{ ...(column.text ? headTextStyle : headStyle), ...sortableStyle }}
                                    onClick={() => toggleSort(column)}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSort(column); } }}
                                    tabIndex={0}
                                    aria-sort={active ? (sort.descending ? 'descending' : 'ascending') : 'none'}
                                >
                                    {column.label}{active && (sort.descending ? ' ▼' : ' ▲')}
                                </th>
                            );
                        })}
                        <th style={headTextStyle}>Returorsak</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, i) => (
                        <ReturnedArticleRow
                            key={row.key}
                            row={row}
                            index={i}
                            name={renderName(row)}
                            invoicesByNumber={invoicesByNumber}
                            showInvoices={showInvoices}
                        />
                    ))}
                </tbody>
            </table>
        </div>
        </>
    );
};

const ReturnedArticleRow = ({ row, index, name, invoicesByNumber, showInvoices }) => {
    const [expanded, setExpanded] = useState(false);
    const [requested, setRequested] = useState(false);
    const article = useMemo(() => ({
        articleNumber: row.articleNumber,
        name: row.description || row.articleNumber,
        invoices: row.returnedInvoices,
    }), [row]);
    const analysisId = `return-analysis-${encodeURIComponent(row.key)}`;

    return (
        <>
            <tr style={{ ...stripeStyle(index), ...(expanded ? { backgroundColor: '#eef6fc' } : {}) }}>
                <td style={textCellStyle}>{name}</td>
                <td style={textCellStyle}>{row.articleNumber}</td>
                <td style={cellStyle}>
                    {row.sold > 0
                        ? <Link onClick={() => showInvoices('Försäljning', row, row.soldInvoices)}>{fmt(row.sold)} st</Link>
                        : `${fmt(row.sold)} st`}
                </td>
                <td style={cellStyle}>
                    <Link onClick={() => showInvoices('Returer', row, row.returnedInvoices)}>{fmt(row.returned)} st</Link>
                </td>
                <td style={cellStyle}>
                    {row.rate === null ? '–' : <span style={rateBadgeStyle(row.rate)}>{fmtPercentPrecise(row.rate)}</span>}
                </td>
                <td style={textCellStyle}>
                    <button
                        type="button"
                        style={analyzeButtonStyle}
                        aria-expanded={expanded}
                        aria-controls={analysisId}
                        onClick={() => { setRequested(true); setExpanded((value) => !value); }}
                    >
                        {expanded ? 'Dölj returorsak' : requested ? 'Visa returorsak' : 'Analysera returorsak'}
                    </button>
                </td>
            </tr>
            {requested && (
                <tr hidden={!expanded} style={{ backgroundColor: '#f6faff' }}>
                    <td colSpan={COLUMNS.length + 1} style={{ ...textCellStyle, padding: '1rem', borderBottom: '1px solid #d0dfea' }}>
                        <div id={analysisId}>
                            <ReturnAnalysis article={article} invoicesByNumber={invoicesByNumber} />
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
};

export default ReturnedArticles;
